import { unref } from 'vue'
import { storeToRefs } from 'pinia'
import { MaybeRef } from '@vueuse/core'
import { useTimers } from '../store/timers'
import { Nullish } from '../types/utility'

type UseStartEndRulesReturn = {
  beforeNow: (date: Date | Nullish) => boolean
  beforeEnd: (date: Date | Nullish) => boolean
  afterStart: (date: Date | Nullish) => boolean
  notBetweenTimers: (date: Date | Nullish, index?: number | undefined) => boolean
  notOverlap: (date: Date | Nullish, position: 'start' | 'end', index?: number) => boolean
  all: (index?: MaybeRef<number>) => boolean
}
export const useStartEndRules = (data: {
  start: MaybeRef<Date>
  end?: MaybeRef<Date>
  index?: MaybeRef<number> /**Index in timer store */
}): UseStartEndRulesReturn => {
  const { getAllParentTimers } = storeToRefs(useTimers())
  const { timeOverlaps, timerConflict } = useTimers()

  /**
   * If time is before now, returns true
   * @param date Date
   * @returns boolean
   */
  const beforeNow = (date: Date | Nullish) => {
    // If the date given is nullish, then it's not a valid date to begin with
    if (!date) return false

    const now = new Date()
    if (date.getTime() > now.getTime()) return false
    return true
  }

  /**
   * Time is before end, returns true.
   * If new time is after end time, returns error message.
   * Runs before end value created
   * @param val Date
   */
  const beforeEnd = (date: Date | Nullish) => {
    // If the date given is nullish, then it's not a valid date to begin with
    if (!date) return false

    if (typeof unref(data.end) === undefined) return true
    else return unref(data.end) ? date <= unref(data.end)! : true
  }
  /**
   * Time is after start, returns true.
   * If new time is before start time, returns error message.
   * Runs before end value created
   * @param val Date
   */
  const afterStart = (date: Date | Nullish) => {
    // If the date given is nullish, then it's not a valid date to begin with
    if (!date) return false

    return unref(data.end) ? date >= unref(data.start) : true
  }

  /**
   * Time is not between timers, returns true.
   * If date is between timers, returns error message.
   * Runs before end value is created
   * @param date Date
   */
  const notBetweenTimers = (
    date: Date | Nullish,
    index: MaybeRef<number> | undefined = data.index
  ) => {
    // If the date given is nullish, then it's not a valid date to begin with
    if (!date) return false

    return !timeOverlaps({
      date: date,
      index: typeof unref(index) !== 'undefined' ? unref(index) : unref(data.index),
    })
  }

  /**
   * If new date (start or end) overlaps any timers, returns error message.
   * It is a wrapper for timerConflict from timers store.
   * @param date Date
   * @param position 'start' | 'end' lets us know which date we are supplying
   */
  const notOverlap = (
    date: Date | Nullish,
    position: 'start' | 'end',
    index: MaybeRef<number> | undefined = data.index
  ) => {
    // If the date given is nullish, then it's not a valid date to begin with
    if (!date) return false

    if (typeof unref(index) === 'undefined') {
      index = getAllParentTimers.value.length - 1
    }
    if (typeof unref(data.end) === 'undefined' || unref(data.end) === null) {
      return true
    }
    return !timerConflict({
      start_time: position === 'start' ? date : unref(data.start),
      end_time: position === 'end' ? date : unref(data.end),
      index: unref(index),
    })
  }
  /**
   * Tests all rule combos. Need to test strict true at the moment.
   * @returns boolean or error string.
   */
  const all = (index?: MaybeRef<number>) => {
    return (
      beforeNow(unref(data.start)) &&
      beforeNow(unref(data.end!)) &&
      beforeEnd(unref(data.start)) &&
      afterStart(unref(data.end!)) &&
      notBetweenTimers(
        unref(data.start),
        typeof index !== 'undefined' ? unref(index) : unref(data.index)
      ) &&
      notBetweenTimers(
        unref(data.end!),
        typeof index !== 'undefined' ? unref(index) : unref(data.index)
      ) &&
      notOverlap(
        unref(data.start),
        'start',
        typeof index !== 'undefined' ? unref(index) : unref(data.index)
      ) &&
      notOverlap(
        unref(data.end!),
        'end',
        typeof index !== 'undefined' ? unref(index) : unref(data.index)
      )
    )
  }
  return {
    beforeNow,
    beforeEnd,
    afterStart,
    notBetweenTimers,
    notOverlap,
    all,
  }
}
